import { LiveAnnouncer } from '@angular/cdk/a11y';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
  AfterViewInit,
  Component,
  ElementRef,
  ViewChild,
  forwardRef,
  inject,
  input,
} from '@angular/core';
import { FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { Observable, combineLatest, map, shareReplay, startWith } from 'rxjs';
import { Ateco, JsonFileService } from 'src/app/services/json-file/json-file.service';

@Component({
  selector: 'app-ateco-selector',
  standalone: false,

  templateUrl: './ateco-selector.component.html',
  styleUrl: './ateco-selector.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AtecoSelectorComponent),
      multi: true,
    },
  ],
})
export class AtecoSelectorComponent implements AfterViewInit {
  private readonly jsonFileService = inject(JsonFileService);
  private readonly announcer = inject(LiveAnnouncer);

  @ViewChild('codAtecoInput') codAtecoInput!: ElementRef<HTMLInputElement>;

  // Input per configurazioni esterne
  required = input<boolean>(false);
  disabled = false;

  // Stato interno
  codiciAtecoCtrl = new FormControl('');
  separatorKeysCodes: number[] = [ENTER, COMMA];
  codiciAtecoSel: Ateco[] = [];
  filteredAteco!: Observable<Ateco[]>;
  showAltriRecordDisponibili = false;
  allAteco$!: Observable<Ateco[]>;

  // Callbacks per ControlValueAccessor
  onChange: any = () => {};
  onTouched: any = () => {};

  constructor() {
    this.allAteco$ = this.jsonFileService.getAteco().pipe(shareReplay(1));
  }

  ngAfterViewInit() {
    this.filteredAteco = combineLatest([
      this.codiciAtecoCtrl.valueChanges.pipe(startWith('')),
      this.allAteco$,
    ]).pipe(
      map(([searchValue, allAteco]) => {
        const filterValue = (typeof searchValue === 'string' ? searchValue : '')
          .toLowerCase()
          .trim();

        const filtered = allAteco.filter(
          (opt) =>
            opt.desc.toLowerCase().includes(filterValue) ||
            opt._id.toLowerCase().includes(filterValue),
        );

        // AGGIORNAMENTO STATO RECORD DISPONIBILI
        this.showAltriRecordDisponibili = filtered.length > 50;

        return filtered;
      }),
    );
  }

  isErrorState() {
    return this.required() && this.codiciAtecoSel.length === 0;
  }

  // Metodi ControlValueAccessor
  writeValue(value: string[]): void {
    if (value && value.length > 0) {
      // Se riceviamo solo ID, recuperiamo gli oggetti completi
      this.jsonFileService.getAtecoById(value).subscribe((atecos) => {
        this.codiciAtecoSel = atecos;
      });
    } else {
      this.codiciAtecoSel = [];
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  remove(atecoId: string): void {
    if (this.disabled) return;

    this.codiciAtecoSel = this.codiciAtecoSel.filter((a) => a._id !== atecoId);
    this.updateModel();
    this.announcer.announce(`Rimosso ${atecoId}`);
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    const selectedAteco = event.option.value as Ateco;
    if (!this.codiciAtecoSel.find((a) => a._id === selectedAteco._id)) {
      this.codiciAtecoSel.push(selectedAteco);
      this.updateModel();
    }
    this.codAtecoInput.nativeElement.value = '';
    this.codiciAtecoCtrl.setValue(null);
  }

  private updateModel() {
    const ids = this.codiciAtecoSel.map((a) => a._id);
    this.onChange(ids);
    this.onTouched();
  }

  // Metodo obbligatorio per gestire lo stato disabled dal Reactive Form genitore
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;

    // Fondamentale: disabilita/abilita il form control interno
    if (isDisabled) {
      this.codiciAtecoCtrl.disable();
    } else {
      this.codiciAtecoCtrl.enable();
    }
  }
}
